import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Flex, Heading, Text, VStack, HStack, Badge, Icon, Card, CardHeader, CardBody, Table, Thead, Tbody, Tr, Th, Td, Button, useColorModeValue
} from '@chakra-ui/react';
import { FiArrowLeft, FiActivity } from 'react-icons/fi';

const FqClaimDetailShell = ({ claimData, rightPanel, backTo = '/fq/pre-submission' }) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const { patient, provider, claim } = claimData;

  return (
    <Box maxW="7xl" mx="auto">
      <Button 
        leftIcon={<FiArrowLeft />} 
        variant="ghost" 
        mb={6} 
        onClick={() => navigate(backTo)}
      >
        Back to Queue
      </Button>

      <Flex justify="space-between" align="flex-end" mb={8}>
        <Box>
          <Heading size="lg" mb={2}>Claim Analysis</Heading>
          <Text color="gray.500">
            Patient: {patient?.first_name} {patient?.last_name} | Provider: {provider?.name}
          </Text>
        </Box>
        <Badge colorScheme="purple" fontSize="md" p={2} borderRadius="md">
          {claimData.type || '837P'}
        </Badge>
      </Flex>

      <Flex gap={8} direction={{ base: 'column', lg: 'row' }}>
        {/* Left Column: Claim Data */}
        <VStack flex="1" spacing={6} align="stretch">
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
            <CardHeader pb={0}>
              <Heading size="md" display="flex" alignItems="center">
                <Icon as={FiActivity} mr={2} color="blue.500" />
                Procedures & Diagnoses
              </Heading>
            </CardHeader>
            <CardBody>
              <Box mb={6}>
                <Text fontWeight="bold" mb={2} color="gray.500" fontSize="sm" textTransform="uppercase">
                  Diagnoses (ICD-10)
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {claim?.diagnoses?.map((dx, i) => (
                    <Badge key={i} colorScheme="blue" variant="subtle" fontSize="sm" p={1}>
                      {dx}
                    </Badge>
                  ))}
                </HStack>
              </Box>
              
              <Box>
                <Text fontWeight="bold" mb={2} color="gray.500" fontSize="sm" textTransform="uppercase">
                  Procedures (CPT)
                </Text>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Code</Th>
                      <Th>Modifiers</Th>
                      <Th isNumeric>Charge</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {claim?.procedures?.map((proc, i) => (
                      <Tr key={i}>
                        <Td fontWeight="medium">{proc.cpt}</Td>
                        <Td>
                          {proc.modifiers?.map(m => (
                            <Badge key={m} colorScheme="orange" mr={1}>{m}</Badge>
                          ))}
                        </Td>
                        <Td isNumeric>${proc.charge?.toFixed(2)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
          
          {/* Clinical Note Section */}
          {claim?.clinical_note && (
            <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
              <CardHeader pb={0}>
                <Heading size="md" display="flex" alignItems="center">
                  Clinical Note Snippet
                </Heading>
              </CardHeader>
              <CardBody>
                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap" p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                  {claim.clinical_note}
                </Text>
              </CardBody>
            </Card>
          )}
        </VStack>

        {/* Right Column: LLM Action Panel */}
        <VStack flex="1" spacing={6} align="stretch">
          {rightPanel}
        </VStack>
      </Flex>
    </Box>
  );
};

export default FqClaimDetailShell;